import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsCategories, FinTechOpsMessages, InitialUserMetricsTabData } from '../../FinTechOpsConstants';
import { UserMetricsDashboardItems } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';
import { UserMetricsParsedDetails } from '../../user-metrics/UserMetricsInterfaces';
import { useUserMetricsData } from '../hooks/useUserMetricsData';

export interface UserMetricsContextValue {
  finTechOpsUserMetricsDataStatus: FiltersStatus;
  setFinTechOpsUserMetricsDataStatus: (s: FiltersStatus) => void;
  userMetricsWithUserData: UserMetricsParsedDetails;
  setUserMetricsWithUserData: (d: UserMetricsParsedDetails) => void;
  finTechOpsUserMetricsDashBoard: UserMetricsDashboardItems;
  setFinTechOpsUserMetricsDashBoard: (d: UserMetricsDashboardItems) => void;
}

const EMPTY_PARSED = {} as UserMetricsParsedDetails;

const defaultValue: UserMetricsContextValue = {
  finTechOpsUserMetricsDataStatus: 'loading',
  setFinTechOpsUserMetricsDataStatus: () => {},
  userMetricsWithUserData: EMPTY_PARSED,
  setUserMetricsWithUserData: () => {},
  finTechOpsUserMetricsDashBoard: InitialUserMetricsTabData,
  setFinTechOpsUserMetricsDashBoard: () => {}
};

const UserMetricsContext = createContext<UserMetricsContextValue>(defaultValue);

export const useUserMetrics = () => useContext(UserMetricsContext);

interface Props {
  children: React.ReactNode;
}

export const UserMetricsProvider = ({ children }: Props) => {
  const { data, loading, error } = useUserMetricsData();
  const { displayNotification } = useNotification();
  const [parsed, setParsed] = useState<UserMetricsParsedDetails>(EMPTY_PARSED);
  const [dashboard, setDashboard] = useState<UserMetricsDashboardItems>(InitialUserMetricsTabData);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.userMetrics} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsUserMetricsDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setUserMetricsWithUserData = useCallback((d: UserMetricsParsedDetails) => setParsed(d), []);
  const setFinTechOpsUserMetricsDashBoard = useCallback((d: UserMetricsDashboardItems) => setDashboard(d), []);

  const value = useMemo<UserMetricsContextValue>(
    () => ({
      finTechOpsUserMetricsDataStatus: status,
      setFinTechOpsUserMetricsDataStatus,
      userMetricsWithUserData: parsed,
      setUserMetricsWithUserData,
      finTechOpsUserMetricsDashBoard: dashboard,
      setFinTechOpsUserMetricsDashBoard
    }),
    [status, parsed, dashboard, setFinTechOpsUserMetricsDataStatus, setUserMetricsWithUserData, setFinTechOpsUserMetricsDashBoard]
  );

  return <UserMetricsContext.Provider value={value}>{children}</UserMetricsContext.Provider>;
};
